/** Suppliers management page. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
} from "@mui/icons-material";
import { Supplier, SupplierStatus, SupplierData, SupplierGatewayCreate } from "../types";
import { suppliersApi } from "../api/client";
import SupplierList from "../components/suppliers/SupplierList";
import SupplierForm from "../components/suppliers/SupplierForm";
import GatewayManageDialog from "../components/suppliers/GatewayManageDialog";
import SupplierRatesDialog from "../components/suppliers/SupplierRatesDialog";

/** Suppliers page with supplier list, form, and gateway management. */
export default function Suppliers() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierData | null>(null);
  const [rateDeckOpen, setRateDeckOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Gateway management state
  const [gatewayDialogOpen, setGatewayDialogOpen] = useState(false);
  const [selectedSupplierForGateways, setSelectedSupplierForGateways] = useState<Supplier | null>(null);

  /** Fetch suppliers from API. Returns the fresh list for callers that need it. */
  const fetchSuppliers = async (): Promise<Supplier[]> => {
    setLoading(true);
    setError("");
    try {
      const response = await suppliersApi.list();
      const items: Supplier[] = response.data.items;
      setSuppliers(items);
      return items;
    } catch (err: any) {
      console.error("Failed to fetch suppliers:", err);
      setError(
        err.response?.data?.detail || "Failed to load suppliers. Please try again."
      );
      return [];
    } finally {
      setLoading(false);
    }
  };

  /** Load suppliers on component mount. */
  useEffect(() => {
    fetchSuppliers();
  }, []);

  /** Handle opening the form for adding a new supplier. */
  const handleAddClick = () => {
    setSelectedSupplier(null);
    setFormOpen(true);
  };

  /** Handle opening the form for editing a supplier. */
  const handleEditClick = (supplier: Supplier) => {
    setSelectedSupplier({
      id: supplier.id,
      name: supplier.name,
      hostname: supplier.hostname,
      status: supplier.status,
      capacity_cps: supplier.capacity_cps,
      capacity_cc: supplier.capacity_cc,
      priority: supplier.priority,
      cgr_subject: supplier.cgr_subject,
      tariff_plan: supplier.tariff_plan,
    });
    setFormOpen(true);
  };

  /** Handle opening the gateway management dialog. */
  const handleManageGateways = (supplier: Supplier) => {
    setSelectedSupplierForGateways(supplier);
    setGatewayDialogOpen(true);
  };

  /** Handle saving a supplier (create or update). */
  const handleSaveSupplier = async (supplierData: SupplierData) => {
    if (selectedSupplier?.id) {
      // Update existing supplier
      await suppliersApi.update(selectedSupplier.id, supplierData);
      setSuccessMessage("Supplier updated successfully");
    } else {
      // Create new supplier
      await suppliersApi.create(supplierData);
      setSuccessMessage("Supplier created successfully");
    }
    await fetchSuppliers();
    setSuccessMessage(
      selectedSupplier?.id ? "Supplier updated successfully" : "Supplier created successfully"
    );
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  /** Handle deleting a supplier. */
  const handleDeleteSupplier = async (supplierId: string) => {
    try {
      await suppliersApi.remove(supplierId);
      setSuccessMessage("Supplier deleted successfully");
      await fetchSuppliers();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      console.error("Failed to delete supplier:", err);
      setError(err.response?.data?.detail || "Failed to delete supplier");
    }
  };

  /** Handle toggling supplier status (enable/disable). */
  const handleToggleStatus = async (supplierId: string, currentStatus: string) => {
    try {
      if (currentStatus === SupplierStatus.ACTIVE) {
        await suppliersApi.disable(supplierId);
        setSuccessMessage("Supplier disabled successfully");
      } else {
        await suppliersApi.enable(supplierId);
        setSuccessMessage("Supplier enabled successfully");
      }
      await fetchSuppliers();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      console.error("Failed to toggle supplier status:", err);
      setError(err.response?.data?.detail || "Failed to update supplier status");
    }
  };

  /** Re-sync the gateway dialog's supplier snapshot after a gateway mutation.
   * The dialog renders `selectedSupplierForGateways.gateways` — a snapshot from
   * when the dialog was opened. fetchSuppliers() only updates the table array,
   * so without this the dialog kept showing the stale list: e.g. "No gateways
   * configured" right after successfully adding the first gateway. */
  const syncSelectedSupplier = (items: Supplier[]) => {
    if (!selectedSupplierForGateways) return;
    const fresh = items.find((s) => s.id === selectedSupplierForGateways.id);
    if (fresh) setSelectedSupplierForGateways(fresh);
  };

  /** Handle creating a new gateway. */
  const handleCreateGateway = async (data: SupplierGatewayCreate) => {
    if (!selectedSupplierForGateways) throw new Error("No supplier selected");
    await suppliersApi.createGateway(selectedSupplierForGateways.id, data);
    syncSelectedSupplier(await fetchSuppliers()); // Refresh to get updated gateways
    setSuccessMessage("Gateway added successfully");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  /** Handle updating a gateway. */
  const handleUpdateGateway = async (gatewayId: number, data: SupplierGatewayCreate) => {
    if (!selectedSupplierForGateways) throw new Error("No supplier selected");
    await suppliersApi.updateGateway(selectedSupplierForGateways.id, gatewayId, data);
    syncSelectedSupplier(await fetchSuppliers()); // Refresh to get updated gateways
    setSuccessMessage("Gateway updated successfully");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  /** Handle deleting a gateway. */
  const handleDeleteGateway = async (gatewayId: number) => {
    if (!selectedSupplierForGateways) throw new Error("No supplier selected");
    await suppliersApi.removeGateway(selectedSupplierForGateways.id, gatewayId);
    syncSelectedSupplier(await fetchSuppliers()); // Refresh to get updated gateways
    setSuccessMessage("Gateway deleted successfully");
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  /** Handle closing the form. */
  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedSupplier(null);
  };

  /** Handle closing the gateway dialog. */
  const handleGatewayDialogClose = () => {
    setGatewayDialogOpen(false);
    setSelectedSupplierForGateways(null);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Suppliers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={loading}
        >
          Add Supplier
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <SupplierList
          suppliers={suppliers}
          onEdit={handleEditClick}
          onDelete={handleDeleteSupplier}
          onToggleStatus={handleToggleStatus}
          onManageGateways={handleManageGateways}
          onRateDeck={(supplier) => {
            setSelectedSupplierForGateways(supplier);
            setRateDeckOpen(true);
          }}
        />
      )}

      {formOpen && (
        <SupplierForm
          open={formOpen}
          onClose={handleFormClose}
          supplier={selectedSupplier}
          onSave={handleSaveSupplier}
        />
      )}

      {selectedSupplierForGateways && (
        <SupplierRatesDialog
          open={rateDeckOpen}
          supplierId={(selectedSupplierForGateways as any).id}
          supplierName={(selectedSupplierForGateways as any).name}
          onClose={() => setRateDeckOpen(false)}
        />
      )}

      {selectedSupplierForGateways && (
        <GatewayManageDialog
          open={gatewayDialogOpen}
          onClose={handleGatewayDialogClose}
          supplierId={selectedSupplierForGateways.id}
          supplierName={selectedSupplierForGateways.name}
          gateways={selectedSupplierForGateways.gateways}
          onCreateGateway={handleCreateGateway}
          onUpdateGateway={handleUpdateGateway}
          onDeleteGateway={handleDeleteGateway}
          supplierStatus={selectedSupplierForGateways.status}
        />
      )}
    </Container>
  );
}
